/*
 * The shareable summary of a finished game.
 *
 * A row of moons, one per round, filling as the guess gets closer — near enough
 * and the moon becomes a star. The point is that someone reading it in a chat
 * window can see the shape of your game without a screenshot and without the
 * answers being given away: it says how far off you were, never where.
 *
 * The ladder is by distance rather than by score, because distance is the thing
 * a reader can picture. It is deliberately generous: anything inside 500 km is a
 * star, since at that range a pin is on top of the answer on any map you can
 * actually see, and only a guess past 8,000 goes fully dark.
 */

import { UNIT_LABEL, toDisplayDistance } from './settings.mjs';

const MOONS = [
  { under: 500, emoji: '⭐' },
  { under: 1000, emoji: '🌕' },
  { under: 2000, emoji: '🌔' },
  { under: 4000, emoji: '🌓' },
  { under: 8000, emoji: '🌒' },
  { under: Infinity, emoji: '🌑' }
];

export function distanceEmoji(km) {
  return MOONS.find((rung) => km < rung.under).emoji;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

/*
 * Trailing slash on purpose. Plenty of chat clients only autolink a bare
 * hostname when they recognise the suffix, and `.space` is new enough that many
 * do not — so the link arrives as dead text. A trailing slash is enough to read
 * it as a URL, and unlike an https:// prefix it does not eat a line of a result
 * people paste into a group chat.
 */
export const SHARE_URL = 'starguessr.sidequests.space/';

/*
 * Deliberately no coordinates and no place names. The last round is a real
 * historical night that everyone playing today gets, so naming it would spoil
 * that round for anyone who reads the message before playing.
 */
export function buildShareText({
  tier,
  rounds,
  total,
  date = new Date(),
  practice = false,
  units = 'km'
}) {
  /*
   * A practice game says so where the date would go. Everyone playing on a given
   * day gets the same daily game, so a date is a claim that the score can be
   * compared with someone else's — which a random one cannot.
   */
  const day = practice ? 'Random' : `${MONTHS[date.getMonth()]} ${date.getDate()}`;
  /*
   * Reported in whatever unit the player reads the game in, but the moon is
   * always chosen off the kilometres: the ladder is a fixed set of thresholds,
   * and running it on miles would quietly make every rung 60% easier.
   */
  const scores = rounds
    .map((entry) => {
      const km = Math.round(entry.result.distanceKm);
      const shown = Math.round(toDisplayDistance(km, units));
      return `${shown.toLocaleString()} ${UNIT_LABEL[units] || 'km'} ${distanceEmoji(km)}`;
    })
    .join('  ');

  return [
    `${SHARE_URL} · ${day} · ${tier}`,
    scores,
    `Final score: ${total.toLocaleString()}`
  ].join('\n');
}
